import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import DayPicker from "react-day-picker";
import "react-day-picker/lib/style.css";
import { useTranslation } from "react-i18next";
import styles from "./TransportationControls.module.scss";

const dateFromValue = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return undefined;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : undefined;
};
const valueFromDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const monthStart = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

export const ModernSelect = ({ value = "", options = [], onChange, placeholder = "Select an option", disabled = false, ariaLabel, searchable, clearable = false, tone, floating = false, popupRef }) => {
  const { t } = useTranslation();
  const root = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [floatingStyle, setFloatingStyle] = useState(null);
  const normalized = useMemo(() => options.map((option) => typeof option === "string" ? { value: option, label: option } : option), [options]);
  const selected = normalized.find((option) => String(option.value) === String(value));
  const filtered = normalized.filter((option) => String(option.label || "").toLowerCase().includes(query.trim().toLowerCase()));
  const useSearch = searchable === true || (searchable !== false && normalized.length > 7);
  useEffect(() => {
    const close = (event) => {
      if (root.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  useLayoutEffect(() => {
    if (!open || !floating) { setFloatingStyle(null); return undefined; }
    const positionMenu = () => {
      if (!triggerRef.current || !menuRef.current) return;
      const trigger = triggerRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const margin = 8;
      const gap = 6;
      const optionsElement = menuRef.current.querySelector(`.${styles.options}`);
      const searchElement = menuRef.current.querySelector(`.${styles.search}`);
      const menuStyles = window.getComputedStyle(menuRef.current);
      const menuChrome =
        parseFloat(menuStyles.paddingTop || 0) +
        parseFloat(menuStyles.paddingBottom || 0) +
        parseFloat(menuStyles.borderTopWidth || 0) +
        parseFloat(menuStyles.borderBottomWidth || 0);
      // Measure the unconstrained content, not the previously constrained popup.
      // Otherwise every sidebar scroll can make the floating menu progressively shorter.
      const optionsHeight = Math.min(optionsElement?.scrollHeight || 0, 250);
      const naturalHeight = Math.ceil(
        menuChrome + (searchElement?.offsetHeight || 0) + optionsHeight
      ) || 180;
      const availableBelow = Math.max(0, viewportHeight - trigger.bottom - gap - margin);
      const availableAbove = Math.max(0, trigger.top - gap - margin);
      const openAbove = availableBelow < naturalHeight && availableAbove > availableBelow;
      const availableHeight = openAbove ? availableAbove : availableBelow;
      const maxHeight = Math.min(naturalHeight, Math.max(0, availableHeight));
      const width = Math.min(Math.max(trigger.width, 180), Math.max(180, viewportWidth - (margin * 2)));
      const left = Math.min(Math.max(margin, trigger.left), Math.max(margin, viewportWidth - width - margin));
      const top = openAbove
        ? Math.max(margin, trigger.top - gap - maxHeight)
        : Math.min(trigger.bottom + gap, Math.max(margin, viewportHeight - maxHeight - margin));
      setFloatingStyle({ position: "fixed", top: Math.round(top), left: Math.round(left), width: Math.round(width), maxHeight: Math.round(maxHeight) });
    };
    positionMenu();
    window.addEventListener("resize", positionMenu);
    document.addEventListener("scroll", positionMenu, true);
    return () => {
      window.removeEventListener("resize", positionMenu);
      document.removeEventListener("scroll", positionMenu, true);
    };
  }, [open, floating, filtered.length, useSearch, clearable]);
  const choose = (next) => { onChange(next); setOpen(false); setQuery(""); };
  const menu = open ? <div ref={(node) => { menuRef.current = node; if (popupRef) popupRef.current = node; }} className={`${styles.menu} ${floating ? styles.floatingMenu : ""}`} style={floating ? floatingStyle || { visibility: "hidden" } : undefined} role="listbox">
    {useSearch ? <div className={styles.search}><span className="material-icons">search</span><input autoFocus value={query} aria-label={t("Search options")} placeholder={t("Search options")} onChange={(event) => setQuery(event.target.value)} /></div> : null}
    <div className={styles.options}>
      {clearable ? <button type="button" role="option" aria-selected={!value} className={!value ? styles.selected : ""} onClick={() => choose("")}><span>{t(placeholder)}</span>{!value ? <span className="material-icons">check</span> : null}</button> : null}
      {filtered.length ? filtered.map((option) => <button type="button" role="option" data-tone={option.tone || undefined} data-swatch={option.swatchColor ? true : undefined} style={option.tintColor ? { backgroundColor: option.tintColor } : undefined} disabled={option.disabled} aria-disabled={option.disabled || undefined} aria-selected={String(option.value) === String(value)} className={String(option.value) === String(value) ? styles.selected : ""} key={String(option.value)} onClick={() => choose(option.value)}><span className={option.swatchColor ? styles.optionLabel : undefined}>{option.swatchColor ? <span className={styles.optionSwatch} style={{ backgroundColor: option.swatchColor }} aria-hidden="true" /> : null}{t(option.label)}</span>{String(option.value) === String(value) ? <span className="material-icons">check</span> : null}</button>) : <span className={styles.noResults}>{t("No matching options")}</span>}
    </div>
  </div> : null;
  return <div ref={root} data-tone={tone || undefined} className={`${styles.select} ${open ? styles.open : ""} ${disabled ? styles.disabled : ""}`}>
    <button ref={triggerRef} type="button" className={styles.trigger} disabled={disabled} aria-label={ariaLabel} aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((current) => !current)}>
      <span className={selected?.swatchColor ? styles.optionLabel : selected ? "" : styles.placeholder}>{selected?.swatchColor ? <span className={styles.optionSwatch} style={{ backgroundColor: selected.swatchColor }} aria-hidden="true" /> : null}{selected ? t(selected.label) : t(placeholder)}</span>
      <span className="material-icons">expand_more</span>
    </button>
    {floating && menu ? createPortal(menu, document.body) : menu}
  </div>;
};

export const ModernDatePicker = ({ value = "", onChange, disabled = false, min, max, ariaLabel, placeholder = "Select date", clearable = true, floating = false }) => {
  const { t, i18n } = useTranslation();
  const root = useRef(null);
  const calendarRef = useRef(null);
  const yearMenuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(() => dateFromValue(value) || dateFromValue(min) || new Date());
  const [floatingStyle, setFloatingStyle] = useState(null);
  const selected = dateFromValue(value);
  const minDate = dateFromValue(min);
  const maxDate = dateFromValue(max);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayDisabled = Boolean((minDate && today < minDate) || (maxDate && today > maxDate));
  useEffect(() => {
    const close = (event) => {
      if (root.current?.contains(event.target) || calendarRef.current?.contains(event.target) || yearMenuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  useEffect(() => {
    if (open) setVisibleMonth(dateFromValue(value) || dateFromValue(min) || new Date());
  }, [open, value, min]);
  useLayoutEffect(() => {
    if (!open || !floating) { setFloatingStyle(null); return undefined; }
    const positionCalendar = () => {
      if (!root.current || !calendarRef.current) return;
      const trigger = root.current.getBoundingClientRect();
      const margin = 8;
      const gap = 6;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const width = Math.min(310, Math.max(240, viewportWidth - (margin * 2)));
      const naturalHeight = calendarRef.current.offsetHeight || 356;
      const availableBelow = Math.max(0, viewportHeight - trigger.bottom - gap - margin);
      const availableAbove = Math.max(0, trigger.top - gap - margin);
      const openAbove = availableBelow < naturalHeight && availableAbove > availableBelow;
      const availableHeight = openAbove ? availableAbove : availableBelow;
      const height = Math.min(naturalHeight, Math.max(220, availableHeight));
      const left = Math.min(Math.max(margin, trigger.right - width), Math.max(margin, viewportWidth - width - margin));
      const top = openAbove
        ? Math.max(margin, trigger.top - gap - height)
        : Math.min(trigger.bottom + gap, Math.max(margin, viewportHeight - height - margin));
      setFloatingStyle({
        position: "fixed",
        top: Math.round(top),
        left: Math.round(left),
        right: "auto",
        bottom: "auto",
        width: Math.round(width),
        maxHeight: Math.round(height),
      });
    };
    positionCalendar();
    window.addEventListener("resize", positionCalendar);
    document.addEventListener("scroll", positionCalendar, true);
    return () => {
      window.removeEventListener("resize", positionCalendar);
      document.removeEventListener("scroll", positionCalendar, true);
    };
  }, [open, floating, visibleMonth, clearable]);
  const visibleMonthStart = monthStart(visibleMonth);
  const previousMonth = new Date(visibleMonthStart.getFullYear(), visibleMonthStart.getMonth() - 1, 1);
  const nextMonth = new Date(visibleMonthStart.getFullYear(), visibleMonthStart.getMonth() + 1, 1);
  const previousDisabled = Boolean(minDate && previousMonth < monthStart(minDate));
  const nextDisabled = Boolean(maxDate && nextMonth > monthStart(maxDate));
  const firstYear = minDate ? minDate.getFullYear() : Math.min(1900, visibleMonth.getFullYear());
  const lastYear = maxDate ? maxDate.getFullYear() : Math.max(2100, visibleMonth.getFullYear());
  const years = Array.from({ length: Math.max(0, lastYear - firstYear + 1) }, (_, index) => firstYear + index);
  const jumpToYear = (year) => {
    let target = new Date(Number(year), visibleMonth.getMonth(), 1);
    if (minDate && target < monthStart(minDate)) target = monthStart(minDate);
    if (maxDate && target > monthStart(maxDate)) target = monthStart(maxDate);
    setVisibleMonth(target);
  };
  const label = selected ? new Intl.DateTimeFormat(i18n.language, { day: "numeric", month: "short", year: "numeric" }).format(selected) : t(placeholder);
  const calendar = open ? <div ref={calendarRef} className={`${styles.calendar} ${floating ? styles.floatingCalendar : ""}`} style={floating ? floatingStyle || { visibility: "hidden" } : undefined} role="dialog" aria-label={ariaLabel || t("Choose date")}>
      <div className={styles.calendarYearFilter}><span>{t("Year")}</span><div className={styles.calendarYearSelect}><ModernSelect ariaLabel={t("Year")} value={String(visibleMonth.getFullYear())} options={years.map(String)} onChange={jumpToYear} searchable floating popupRef={yearMenuRef} /></div></div>
      <div className={styles.calendarNavigation}>
        <button type="button" disabled={previousDisabled} aria-label={t("Previous month")} onMouseDown={(event) => event.preventDefault()} onClick={() => setVisibleMonth(previousMonth)}><span className="material-icons">chevron_left</span></button>
        <button type="button" disabled={nextDisabled} aria-label={t("Next month")} onMouseDown={(event) => event.preventDefault()} onClick={() => setVisibleMonth(nextMonth)}><span className="material-icons">chevron_right</span></button>
      </div>
      <DayPicker selectedDays={selected} month={visibleMonth} onMonthChange={setVisibleMonth} onDayClick={(day, modifiers) => { if (!modifiers.disabled) { onChange(valueFromDate(day)); setOpen(false); } }} disabledDays={{ before: minDate, after: maxDate }} firstDayOfWeek={1} />
      <footer><button type="button" disabled={todayDisabled} onClick={() => { onChange(valueFromDate(today)); setOpen(false); }}>{t("Today")}</button>{clearable ? <button type="button" onClick={() => { onChange(""); setOpen(false); }}>{t("Clear")}</button> : null}</footer>
    </div> : null;
  return <div ref={root} className={`${styles.datePicker} ${open ? styles.open : ""} ${disabled ? styles.disabled : ""}`}>
    <button type="button" className={styles.trigger} disabled={disabled} aria-label={ariaLabel} aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen((current) => !current)}><span className="material-icons">calendar_month</span><span className={selected ? "" : styles.placeholder}>{label}</span><span className="material-icons">expand_more</span></button>
    {floating && calendar ? createPortal(calendar, document.body) : calendar}
  </div>;
};
